import { useParams, Link, Navigate } from "react-router-dom";
import { Stethoscope, Activity, HandHeart, MessageCircle, ShieldCheck, Heart, Users, CheckCircle2, ArrowRight, Phone } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { JsonLd } from "@/components/JsonLd";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { TrustBar } from "@/components/TrustBar";
import { ConsultationCTA } from "@/components/ConsultationCTA";
import { LeadForm } from "@/components/LeadForm";
import { Reveal } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SERVICES, getService } from "@/data/services";
import { AREAS } from "@/data/areas";
import { COMPANY } from "@/lib/constants";

const ICONS = { Stethoscope, Activity, HandHeart, MessageCircle, ShieldCheck, Heart, Users };

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getService(slug);
  useSEO({
    title: service?.metaTitle,
    description: service?.metaDescription,
    canonical: `/services/${slug}`,
  });

  if (!service) return <Navigate to="/services" replace />;

  const Icon = ICONS[service.icon] || Stethoscope;
  const related = service.related.map(getService).filter(Boolean);

  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "MedicalProcedure",
    name: service.name,
    description: service.metaDescription,
    url: `https://esprithomecare.com/services/${slug}`,
    provider: {
      "@type": "HomeHealthCareService",
      name: "Esprit Home Healthcare",
      telephone: "+1-703-998-7400",
      areaServed: AREAS.map((a) => `${a.name}, VA`),
    },
  };
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: service.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div>
      <JsonLd id="service" data={serviceLd} />
      <JsonLd id="faq" data={faqLd} />

      {/* Hero */}
      <section className="bg-[hsl(var(--muted))] border-b border-border">
        <div className="container-page py-10 lg:py-14">
          <Breadcrumbs items={[{ label: "Services", to: "/services" }, { label: service.shortName }]} />
          <div className="grid lg:grid-cols-2 gap-10 items-center mt-6">
            <div>
              <span className="grid place-items-center w-16 h-16 rounded-2xl bg-primary text-primary-foreground">
                <Icon className="w-8 h-8" />
              </span>
              <h1 className="font-heading font-extrabold text-4xl md:text-5xl text-primary mt-5 leading-tight">{service.name}</h1>
              <p className="font-heading text-2xl text-accent mt-2">{service.tagline}</p>
              <p className="text-lg text-foreground/80 mt-4 leading-relaxed">{service.intro}</p>
              <div className="flex flex-col sm:flex-row gap-4 mt-7">
                <a href={COMPANY.phoneHref} data-testid="service-call-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-accent hover:bg-accent/90 text-accent-foreground h-14 px-7 text-lg font-bold transition-colors">
                  <Phone className="w-5 h-5" /> Call {COMPANY.phone}
                </a>
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground h-14 px-7 text-lg font-bold transition-colors">
                  Free Consultation <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
            <img src={service.image} alt={service.name} className="rounded-[2rem] w-full h-[380px] object-cover shadow-lg" />
          </div>
        </div>
      </section>

      <TrustBar />

      {/* Body */}
      <section className="container-page py-16 lg:py-20 grid lg:grid-cols-3 gap-12 items-start">
        <div className="lg:col-span-2 space-y-12">
          <Reveal>
            <h2 className="font-heading font-bold text-3xl text-primary">Overview</h2>
            {service.overview.map((p, i) => (
              <p key={i} className="text-lg text-foreground/80 leading-relaxed mt-4">{p}</p>
            ))}
          </Reveal>

          <Reveal>
            <h2 className="font-heading font-bold text-3xl text-primary">What's Included</h2>
            <div className="grid sm:grid-cols-2 gap-3 mt-5">
              {service.benefits.map((b) => (
                <div key={b} className="flex items-start gap-3 bg-card rounded-xl border border-border px-5 py-4">
                  <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <span className="font-medium">{b}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal>
            <div className="bg-[hsl(var(--muted))] rounded-[2rem] p-8">
              <h2 className="font-heading font-bold text-3xl text-primary">Is this right for your family?</h2>
              <p className="text-muted-foreground mt-2">This service is a great fit when you're:</p>
              <ul className="grid sm:grid-cols-2 gap-3 mt-5">
                {service.conditions.map((c) => (
                  <li key={c} className="flex items-start gap-3 text-foreground/90">
                    <ArrowRight className="w-5 h-5 text-accent shrink-0 mt-1" /> {c}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal>
            <h2 className="font-heading font-bold text-3xl text-primary">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="mt-4">
              {service.faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-lg font-semibold">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-base text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>

        {/* Sidebar */}
        <div className="lg:sticky lg:top-28 space-y-6">
          <div className="bg-card rounded-[2rem] border border-border p-6">
            <h3 className="font-heading font-bold text-2xl text-primary mb-1">Request Care</h3>
            <p className="text-muted-foreground text-sm mb-4">We'll respond promptly to plan your care.</p>
            <LeadForm source={`service-${slug}`} compact />
          </div>

          <div className="bg-card rounded-2xl border border-border p-6">
            <h3 className="font-heading font-bold text-xl text-primary mb-3">Related Services</h3>
            <ul className="space-y-2">
              {related.map((r) => (
                <li key={r.slug}>
                  <Link to={`/services/${r.slug}`} className="flex items-center justify-between gap-2 text-foreground/90 hover:text-primary group">
                    {r.shortName} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-card rounded-2xl border border-border p-6">
            <h3 className="font-heading font-bold text-xl text-primary mb-3">Areas We Serve</h3>
            <div className="flex flex-wrap gap-2">
              {AREAS.slice(0, 8).map((a) => (
                <Link key={a.slug} to={`/service-areas/${a.slug}`} className="rounded-full bg-muted hover:bg-primary hover:text-primary-foreground px-3 py-1.5 text-sm transition-colors">
                  {a.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* All services internal links */}
      <section className="bg-[hsl(var(--muted))]">
        <div className="container-page py-14">
          <h2 className="font-heading font-bold text-2xl text-primary mb-6">Explore All Our Services</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {SERVICES.map((s) => {
              const SIcon = ICONS[s.icon] || Stethoscope;
              return (
                <Link key={s.slug} to={`/services/${s.slug}`}
                  className={`flex items-center gap-3 rounded-xl border px-4 py-3 transition-colors ${s.slug === slug ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary"}`}>
                  <SIcon className="w-5 h-5 shrink-0" /> <span className="font-medium">{s.shortName}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <ConsultationCTA subtitle={`Talk to our team about ${service.shortName.toLowerCase()} in Northern Virginia. We verify your insurance at no cost.`} />
    </div>
  );
}
